// Month-to-date activity counters for the sidebar usage row and
// /settings/usage (docs/09 §4 "Usage row"; UI-LAW §8). Pure transforms only:
// the `usage_units` metrics:query envelope → the three activity counters,
// plus the UTC calendar-month window math the queries and the period picker
// share. This module derives activity only; hosted credit meters derive from
// the separate entitlement projection in their feature-layer containers.

import type { components } from "../api/schema";
import type { MetricsQueryResponse, TimeWindow } from "./dashboardMetrics";

type UsageEventType = components["schemas"]["UsageEventType"];

// --- counters ------------------------------------------------------------

export type UsageCounterId =
  | "ingested-minutes"
  | "simulated-minutes"
  | "transcripts";

export interface UsageCounterDef {
  // The CLOSED `usage_events.event_type` values this counter sums
  // (docs/03 §12). "Transcripts" counts both ingest shapes: uploaded
  // transcript documents and submitted conversations.
  eventTypes: readonly UsageEventType[];
  id: UsageCounterId;
  label: string;
}

export const USAGE_ACTIVITY_COUNTERS: readonly UsageCounterDef[] = [
  {
    eventTypes: ["simulated_minutes"],
    id: "simulated-minutes",
    label: "Simulated minutes",
  },
  {
    eventTypes: ["ingested_voice_minutes"],
    id: "ingested-minutes",
    label: "Ingested minutes",
  },
  {
    eventTypes: ["ingested_transcript", "submitted_conversation"],
    id: "transcripts",
    label: "Transcripts",
  },
];

// The `usage_units` metric's one measure key (core metrics registry).
const USAGE_QUANTITY_MEASURE = "quantity";

export interface UsageActivityCounter {
  id: UsageCounterId;
  label: string;
  quantity: number;
}

// The three counters from a `usage_units` response grouped by `event_type`
// (`bucket: none`). A missing group is a true zero — the query succeeded and
// no events of that type fell in the window; error states never reach here
// (the callers render an error note instead of counters).
export function usageCountersFromResponse(
  response: MetricsQueryResponse,
): UsageActivityCounter[] {
  const byEventType = new Map<string, number>();
  const series = (response.series ?? []).find(
    (candidate) => candidate.key === USAGE_QUANTITY_MEASURE,
  );
  for (const group of series?.groups ?? []) {
    const eventType = (group.dimensions ?? []).find(
      (coordinate) => coordinate.dimension === "event_type",
    )?.value;
    if (eventType === undefined) {
      continue;
    }
    const total = (group.points ?? []).reduce(
      (sum, point) => sum + (point.kind === "scalar" ? point.value : 0),
      0,
    );
    byEventType.set(eventType, (byEventType.get(eventType) ?? 0) + total);
  }
  return USAGE_ACTIVITY_COUNTERS.map((def) => ({
    id: def.id,
    label: def.label,
    quantity: def.eventTypes.reduce(
      (sum, eventType) => sum + (byEventType.get(eventType) ?? 0),
      0,
    ),
  }));
}

// --- calendar months (UTC — usage_events are timestamptz) ------------------

const MONTH_KEY_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isMonthKey(value: unknown): value is string {
  return typeof value === "string" && MONTH_KEY_PATTERN.test(value);
}

export function monthKeyForDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  return `${String(year)}-${String(month).padStart(2, "0")}`;
}

export function currentMonthKey(now: Date = new Date()): string {
  return monthKeyForDate(now);
}

// The half-open UTC window [month start, next month start) — the same period
// convention as the signed usage export (docs/08 §XIV).
export function monthWindowFromKey(key: string): TimeWindow {
  const match = MONTH_KEY_PATTERN.exec(key);
  if (!match) {
    throw new Error(`Not a calendar-month key: ${key}`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  return {
    // Date.UTC rolls month 12 into January of the next year.
    end: new Date(Date.UTC(year, month, 1)).toISOString(),
    start: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
  };
}

// The period picker's options: the current month first, then the previous
// `count - 1` months, all as month keys.
export function recentMonthKeys(
  count: number,
  now: Date = new Date(),
): string[] {
  const keys: string[] = [];
  for (let offset = 0; offset < count; offset += 1) {
    keys.push(
      monthKeyForDate(
        new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1)),
      ),
    );
  }
  return keys;
}

export function monthLabel(key: string): string {
  const window = monthWindowFromKey(key);
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(window.start));
}
